import { CONFIGURATION_APIS } from "@/api_framework/api_config";
import { IClinicDoctorListResponse, IClinicDoctorOption } from "@/api_framework/api_modals/Configuration";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import axios, { AxiosError } from "axios";
import { useCallback, useState } from "react";
import AxiosCurlirize from "axios-curlirize";

AxiosCurlirize(axios, () => {});

// Provider category ids for doctors — the providers endpoint is shared across
// staff types, so we scope the fetch to doctor categories only.
const DOCTOR_PROVIDER_CATEGORY_IDS = ["7", "31"];

// Doctors selectable for a clinic's preferred-staff mapping. Returns options
// keyed by staff_id (clinic_org_app_staffprofile id) — the same id the mapping
// stores — so the picker can't feed a mismatched id into the mapping.
export const useClinicDoctorListAPI = () => {
  const configuration = useConfiguration();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();
  const [doctorList, setDoctorList] = useState<IClinicDoctorOption[]>([]);

  const execute = useCallback(
    async (clinicId: string) => {
      if (!clinicId) {
        setDoctorList([]);
        return [];
      }
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const response = await axios.post<IClinicDoctorListResponse>(
          CONFIGURATION_APIS.SERVICE_PROVIDERS.baseURL,
          {
            provider_category_ids: DOCTOR_PROVIDER_CATEGORY_IDS,
            clinic_ids: [clinicId],
            source: "rota",
          },
          {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );
        const doctors = response.data?.data ?? [];
        setDoctorList(doctors);
        return doctors;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          captureError(axiosError);
          if (axiosError.config) {
            sendCurl(axiosError.config["curlCommand"]);
          }
        }
        setDoctorList([]);
        return [];
      }
    },
    [configuration, captureError, sendCurl],
  );

  return { execute, doctorList };
};
